import type { CatalogTaxonomyGroup } from './catalogTaxonomy'
import { CATALOG_TAXONOMY_GROUPS } from './catalogTaxonomy'

export interface CategoryGroup {
  readonly name: string
  /** Present, real leaf categories for this group, in the taxonomy's own
   * leaf order (not necessarily the order `GET /catalog/categories` returned
   * them in). */
  readonly categories: readonly string[]
}

export interface GroupedCategories {
  /** Never includes a group with zero present leaf categories (UID-253:
   * "don't render a group tile if none of its leaf categories are present in
   * the real data — empty groups look broken") or exactly one present leaf
   * category (UID-520: single-category groups are folded into `other`
   * instead — see `groupCategories`'s doc comment). */
  readonly groups: readonly CategoryGroup[]
  /** Real categories that matched no taxonomy leaf, plus any taxonomy
   * group's lone category (UID-520), alphabetized together — the
   * clearly-labeled catch-all so a category is never silently dropped
   * (UID-253) and a 1-category group never renders as its own confusing
   * standalone card (UID-520). */
  readonly other: readonly string[]
}

/**
 * Buckets the REAL catalog category labels (`GET /catalog/categories`) under
 * `CATALOG_TAXONOMY_GROUPS` (or an injected taxonomy, for tests). Pure and
 * side-effect free — the data-fetch lives in `useCategoryTaxonomy.ts`.
 *
 * Matching is exact-match/case-sensitive against each group's leaf list (the
 * same exactness `CategoryFilterBar`'s `category` query param already
 * assumes). If a leaf name were ever hypothetically listed under two groups,
 * the first group in the taxonomy array claims it — a later group re-listing
 * an already-claimed name just won't see it (and may end up empty and get
 * omitted, same as any other empty group).
 *
 * UID-520: a taxonomy group that resolves to exactly one present leaf
 * category is folded into `other` rather than rendered as its own
 * standalone card — verbatim UX feedback was "maybe we chuck anything that
 * has only 1 category into the 'Other' group ... I don't want to confuse
 * customers on why they see something in a category." This is a simple
 * post-grouping move (drop the 1-category group, merge its lone category
 * into `other`), not a re-bucketing pass — it never cascades, since
 * removing a whole group can't push any *other* group's count down to 1.
 */
export function groupCategories(
  realCategories: readonly string[],
  taxonomy: readonly CatalogTaxonomyGroup[] = CATALOG_TAXONOMY_GROUPS,
): GroupedCategories {
  const realSet = new Set(realCategories)
  const covered = new Set<string>()
  const groups: CategoryGroup[] = []

  for (const group of taxonomy) {
    const present = group.categories.filter(
      (category) => realSet.has(category) && !covered.has(category),
    )
    for (const category of present) covered.add(category)
    if (present.length > 0) groups.push({ name: group.name, categories: present })
  }

  const singleCategoryOverflow: string[] = []
  const multiCategoryGroups = groups.filter((group) => {
    const [onlyCategory] = group.categories
    if (group.categories.length === 1 && onlyCategory !== undefined) {
      singleCategoryOverflow.push(onlyCategory)
      return false
    }
    return true
  })

  const other = realCategories
    .filter((category) => !covered.has(category))
    .concat(singleCategoryOverflow)
    .sort((a, b) => a.localeCompare(b))

  return { groups: multiCategoryGroups, other }
}
